import { DateTime, Duration, DurationLike } from "luxon";
import moment from "moment";

declare module 'luxon' {
    export interface DateTime {
        getRelativeDate(relativeDateString: string): DateTime;
    }
}

declare global {
    interface String {
        format(...args: any[]): string;
        isObjectId(): boolean;
        padLeft(size: number, char: string): string;
        toCurrency(): string;
        before(str: string): string;
        after(str: string): string;
    }

    interface Date {
        toFormattedString(format: string): string;
        convertRelativeDateString(relativeDateString: string): Date;
    }

    interface Array<T> {
        findByFunction(func: any, findOne: boolean): T;
        findByPropertyValue(propertyName: any, propertyValue: any, findOne: boolean): T;
        findOne(propertyName: any, propertyValue: any): T;
    }
}

Object.defineProperty(String.prototype, 'format', {
    value: function (this: string) {
        let template = this;
        if (!template) {
          return template;
        }

        let params = [...arguments];  // Array.prototype.slice.call(arguments, 1);
        if (!!params && params.length > 0) {
            for (let i = 0; i < params.length; i++) {
                template = template.replace('{' + i + '}', params[i] || '');
            }
        }

        return template;
    }
});

Object.defineProperty(String.prototype, 'isObjectId', {
    value: function (this: string) {
        return (/[a-f0-9]{24}/i).exec(this);
    }
});

Object.defineProperty(String.prototype, 'padLeft', {
    value: function (this: string, size: number, char: string) {
        var result = String(this);
        if (char === null) {
            char = ' ';
        }

        while (result.length < size) {
            result = char + result;
        }

        return result;
    }
});

Object.defineProperty(String.prototype, 'toCurrency', {
    value: function (this: string, size: number, char: string) {
        try {
            let floatVal = parseFloat(this).toFixed(2);
            let strVal = '$' + floatVal.toString();
            return strVal;
        }
        catch (err) {
            return this;
        }
    }
});

Object.defineProperty(String.prototype, 'before', {
    value: function (this: string, str: string) {
        var i = this.indexOf(str);
        return i !== -1 ? this.substring(0, i) : '';
    }
});

Object.defineProperty(String.prototype, 'after', {
    value: function (this: string, str: string) {
        var i = this.indexOf(str);
        return i !== -1 ? this.substring(this.length - (this.length - (i + 1))) : '';
    }
});

Object.defineProperty(Date.prototype, 'convertRelativeDateString', {
    value: function (this: Date, relativeDateString: string) {
        let date: Date = this;
        function add(date: Date, unit, amount) {
            // Deprecation warning: moment().add(period, number) is deprecated. Please use moment().add(number, period).
            // See http://momentjs.com/guides/#/warnings/add-inverted-param/ for more info.
            // return moment(date).startOf('day').add(unit, amount).toDate();
            return moment(date).startOf('day').add(amount, unit).toDate();
        }

        // Return empty string if "" is supplied
        if (relativeDateString === "") {
            return "";
        }

        // Return today's date if "0" is supplied
        if (relativeDateString === "0") {
            return date;
        }

        // If an actual date is supplied, return that
        if (!relativeDateString.startsWith("+") &&
            !relativeDateString.startsWith("-")) {
            var isoDateStr = relativeDateString.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3");
            if (!isNaN(Date.parse(isoDateStr))) {
                return new Date(isoDateStr);
            }

            if (!isNaN(Date.parse(relativeDateString))) {
                return new Date(relativeDateString);
            }
        }

        if (!moment) {
            throw "Need to install Moment.js to use this function";
        }

        // Otherwise try to parse the relative date with Moment
        var regex = new RegExp("([+-]?)([0-9]+)([A-Z]?[A-Z]?)", "ig"),
            match;

        while ((match = regex.exec(relativeDateString)) !== null) {
            if (!match[3]) {
                match[3] = 'd';
            }
            else if (match[3] === 'm') {
                match[3] = match[3].toUpperCase();
            }
            else if (match[3] !== 'M') {
                match[3] = match[3].toLowerCase();
            }
            date = add(date, match[3], match[1] + match[2]);
        }

        return date;
    }
});

Object.defineProperty(Date.prototype, 'toFormattedString', {
    value: function (this: Date, format: string) {
        const date = this;
        if (!date) {
            return "";
        }

        let daysAbbrev = ['Sun', 'Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat'],
            days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            monthsAbbrev = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
            year = date.getFullYear(),
            yearAbbrev = year.toString().substring(2, 2),
            month = date.getMonth() + 1,
            monthPad = month.toString().padLeft(2, '0'),
            monthAbbrev = monthsAbbrev[month - 1],
            monthFull = months[month - 1],
            dayofWeek = date.getDay(),
            day = date.getDate(),
            dayPad = day.toString().padLeft(2, '0'),
            dayAbbrev = daysAbbrev[dayofWeek],
            dayFull = days[dayofWeek],
            hourMilitary = date.getHours(),
            hourMilitaryPad = hourMilitary.toString().padLeft(2, '0'),
            hourAmpm = (hourMilitary === 0) ? 12 : (hourMilitary > 12) ? hourMilitary - 12 : hourMilitary,
            hourAmpmPad = hourAmpm.toString().padLeft(2, '0'),
            min = date.getMinutes(),
            minPad = min.toString().padLeft(2, '0'),
            sec = date.getSeconds(),
            secPad = sec.toString().padLeft(2, '0'),
            timeDesignator = format.indexOf('h') === -1 ? '' : hourMilitary < 12 ? ' AM' : ' PM';

        return format.replace('dddd', dayFull)
            .replace('ddd', dayAbbrev)
            .replace('dd', dayPad)
            .replace(/d(?![a-zA-Z])/, day.toString())
            .replace('MMMM', monthFull)
            .replace('MMM', monthAbbrev)
            .replace('MM', monthPad)
            .replace(/M(?![a-zA-Z])/, month.toString())
            .replace('yyyy', year.toString())
            .replace('yy', yearAbbrev)
            .replace('ss', sec.toString())
            .replace(/s(?![a-zA-Z])/, secPad)
            .replace('mm', minPad)
            .replace(/m(?!b)/, min.toString())
            .replace('HH', hourMilitaryPad)
            .replace('H', hourMilitary.toString())
            .replace('hh', hourAmpmPad)
            .replace(/h(?![a-zA-Z])/, hourAmpm.toString())
            .replace('tt', timeDesignator);
    }
});

Object.defineProperty(DateTime.prototype, 'getRelativeDate', {
    value: function (this: DateTime, relativeDateString: string): DateTime
    // DateTime.prototype.getRelativeDate = function(this: DateTime, relativeDateString: string): DateTime
    {
        /* ----------------------------------------------------------------------------------------------------------------------
        * Gets a date relative to the date supplied, using a string parsed with the moment library
        *
        * @param  {String} relativeDateString               The relative date string string.
        * @return {Date}                      The relative date.
        * ----------------------------------------------------------------------------------------------------------------------*/
        let date = DateTime.now();

        // Return empty string if "" is supplied
        if (relativeDateString === '') {
            return null;
        }

        // Return today's date if "0" is supplied
        if (relativeDateString === '0') {
            return date;
        }

        // If an actual date is supplied, return that
        if (!relativeDateString.startsWith("+") && !relativeDateString.startsWith("-")) {
            let isoDateStr = relativeDateString.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$2-$1");
            if (!isNaN(Date.parse(isoDateStr))) {
                return DateTime.fromISO(isoDateStr);
            }

            const milliseconds = Date.parse(relativeDateString);
            if (!isNaN(milliseconds)) {
                return DateTime.fromMillis(milliseconds);
            }
        }

        // Otherwise try to parse the relative date with Luxon
        const regex = /([+-]?)(\d+)([A-Z]?[A-Z]?)/ig;
        let match;
        let duration: DurationLike = {};

        while ((match = regex.exec(relativeDateString)) !== null) {
            let value = +(match[1] + match[2]);
            let prop = match[3];

            if (!prop) {
                prop = 'd';
            }

            prop = prop.toLowerCase();

            switch (prop) {
                case 'd':
                    duration.days = value;
                    break;
                case 'w':
                    duration.weeks = value;
                    break;
                case 'm':
                    duration.months = value;
                    break;
                case 'y':
                    duration.years = value;
                    break;
                default:
                    break;
            }

            date = date.startOf('day').plus(Duration.fromObject(duration));
        }

        return date;
    }
});

Object.defineProperty(Array.prototype, 'findByFunction', {
    value: function <T>(this: Array<T>, func: any, findOne: boolean) {
        var arr = [];
        for (var i = 0; i < this.length; i++) {
            var item = this[i];
            if (func(item)) {
                if (findOne) {
                    return item;
                }
                else {
                    arr.push(item);
                }
            }
        }
        return findOne ? null : arr;
    }
});

Object.defineProperty(Array.prototype, 'findByPropertyValue', {
    value: function <T>(this: Array<T>, propertyName: any, propertyValue: any, findOne: boolean) {
        var arr = [];
        for (var i = 0; i < this.length; i++) {
            var item = this[i];
            if (item.hasOwnProperty(propertyName) && item[propertyName] === propertyValue) {
                if (findOne) {
                    return item;
                }
                else {
                    arr.push(item);
                }
            }
        }
        return findOne ? null : arr;
    }
});

Object.defineProperty(Array.prototype, 'findOne', {
    value: function <T>(this: Array<T>, propertyName: any, propertyValue: any) {
        var func = typeof propertyName !== "undefined" && typeof propertyValue === "undefined" && typeof propertyName === 'function' ? propertyName : undefined;
        if (func) {
            return this.findByFunction.call(this, func, true);
        }
        if (typeof propertyName !== "undefined" && propertyValue !== "undefined") {
            return this.findByPropertyValue.call(this, propertyName, propertyValue, true);
        }

        return undefined;
    }
});

Object.defineProperty(Array.prototype, 'intersect', {
    value: function <T>(this: Array<T>, input: Array<T>) {
        let intersection = this.filter(x => input.includes(x));
        return intersection;
    }
});

export { };
